import { useForm, zodResolver } from "@mantine/form";

import {
  LABOR_QUESTIONNAIRE_DISPLAY_NAMES,
  QuestionnaireType,
} from "@/encounters/components/EncounterLabor/constants/labor-questionnaires";
import { LaborStageSelectField } from "@/encounters/components/EncounterLabor/EncounterLaborAmend/LaborStageSelectField";
import { MembranesFormFields } from "@/encounters/components/EncounterLabor/flowsheet-forms/membranes/components/MembranesFormFields";
import {
  MEMBRANES_DATE_LINK_ID,
  MEMBRANES_DATE_TIME_LINK_ID,
  MEMBRANES_QUESTIONNAIRE_NAME,
  MEMBRANES_ROOT_LINK_ID,
  MEMBRANES_TIME_LINK_ID,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/membranes/constants/membranes-link-ids";
import type { MembranesFlowsheetDrawerProps } from "@/encounters/components/EncounterLabor/flowsheet-forms/membranes/types/membranes-drawer";
import type { MembranesFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/membranes/types/membranes-form";
import {
  getDefaultMembranesFlowsheetFormValues,
  normalizeMembranesFlowsheetFormValues,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/membranes/utils/membranes-form-defaults";
import {
  mapMembranesFormValuesToQuestionnaireValues,
  mapQuestionnaireResponseToMembranesFormValues,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/membranes/utils/membranes-questionnaire-mapping";
import { membranesFlowsheetFormSchema } from "@/encounters/components/EncounterLabor/flowsheet-forms/membranes/zod-schemas/membranes-flowsheet-form";
import { LaborFlowsheetDrawerShell } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/LaborFlowsheetDrawerShell";
import {
  type LaborFlowsheetDrawerEditContext,
  useLaborFlowsheetDrawerPersist,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/useLaborFlowsheetDrawerPersist";

const MEMBRANES_DISPLAY_NAME = LABOR_QUESTIONNAIRE_DISPLAY_NAMES[QuestionnaireType.Membranes];

/** Builds edit-mode persist context from drawer props; undefined for quick-add. */
function getMembranesEditContext(
  props: MembranesFlowsheetDrawerProps,
): LaborFlowsheetDrawerEditContext | undefined {
  if (props.mode !== "edit") {
    return undefined;
  }

  return {
    observation: props.observation,
    questionnaireDisplayName: props.questionnaireDisplayName,
  };
}

export function mergeMembranesHydratedEditFormValues({
  mapped,
  hydratedQuestionnaireValues,
}: {
  mapped: MembranesFlowsheetFormValues;
  hydratedQuestionnaireValues: Record<string, unknown>;
}): MembranesFlowsheetFormValues {
  const hydratedRoot = hydratedQuestionnaireValues[MEMBRANES_ROOT_LINK_ID] as
    | Record<string, unknown>
    | undefined;
  const hydratedDateTime = hydratedRoot?.[MEMBRANES_DATE_TIME_LINK_ID] as
    | Record<string, unknown>
    | undefined;
  const hydratedExamDate = hydratedDateTime?.[MEMBRANES_DATE_LINK_ID];
  const hydratedExamTime = hydratedDateTime?.[MEMBRANES_TIME_LINK_ID];

  return {
    ...mapped,
    examDate: hydratedExamDate instanceof Date ? hydratedExamDate : mapped.examDate,
    examTime: typeof hydratedExamTime === "string" ? hydratedExamTime : mapped.examTime,
  };
}

export function MembranesFlowsheetDrawer(props: MembranesFlowsheetDrawerProps) {
  const { opened, onClose, clientId, encounterId, mode } = props;

  const form = useForm<MembranesFlowsheetFormValues>({
    initialValues: getDefaultMembranesFlowsheetFormValues(),
    validate: zodResolver(membranesFlowsheetFormSchema),
  });

  const {
    isLoading,
    isSubmitting,
    loadError,
    dateTimeUtcDateAnchors,
    drawerLaborStageLoincCode,
    setDrawerLaborStageLoincCode,
    showLaborStageInDrawer,
    drawerTitle,
    handleSubmit,
  } = useLaborFlowsheetDrawerPersist<MembranesFlowsheetFormValues>({
    opened,
    mode,
    onClose,
    clientId,
    encounterId,
    questionnaireName: MEMBRANES_QUESTIONNAIRE_NAME,
    displayName: MEMBRANES_DISPLAY_NAME,
    timerKey: "membranes",
    form,
    parseAndNormalizeFormValues: (values) =>
      normalizeMembranesFlowsheetFormValues(membranesFlowsheetFormSchema.parse(values)),
    getDefaultFormValues: getDefaultMembranesFlowsheetFormValues,
    mapFormValuesToQuestionnaireValues: mapMembranesFormValuesToQuestionnaireValues,
    mapQuestionnaireResponseToFormValues: mapQuestionnaireResponseToMembranesFormValues,
    mergeHydratedEditFormValues: mergeMembranesHydratedEditFormValues,
    questionnaireNotFoundMessage: "Membranes questionnaire not found.",
    editEntryNotFoundMessage: "Could not load existing membranes entry.",
    editContext: getMembranesEditContext(props),
    onRefresh: mode === "quick-add" ? props.onRefresh : undefined,
    title: props.title,
  });

  return (
    <LaborFlowsheetDrawerShell
      opened={opened}
      onClose={onClose}
      error={loadError}
      title={drawerTitle}
      isLoading={isLoading}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      dateTimeUtcDateAnchors={dateTimeUtcDateAnchors}
      formId={`labor-flowsheet-form-${QuestionnaireType.Membranes}`}
      submitButtonText={mode === "edit" ? "Update" : "Add To Flowsheet"}
      headerSection={
        showLaborStageInDrawer && drawerLaborStageLoincCode ? (
          <LaborStageSelectField
            value={drawerLaborStageLoincCode}
            onChange={setDrawerLaborStageLoincCode}
          />
        ) : null
      }
    >
      <MembranesFormFields form={form} mode={mode} clientId={clientId} isDrawerOpened={opened} />
    </LaborFlowsheetDrawerShell>
  );
}
